import Button, { buttonClasses } from '@ahamove/web-components/lib/Button';
import Grid from '@ahamove/web-components/lib/Grid';
import Typography from '@ahamove/web-components/lib/Typography';
import dayjs from 'dayjs';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { useGetUserProfile } from '@/api/auth/useGetUserProfile';
import type { AvailableCity } from '@/api/service/useGetAvailableCities';
import { useDateTimeStore } from '@/global-stores/useDateTimeStore';
import { useLocationStore } from '@/global-stores/useLocationStore';
import { detectPlatform } from '@/lib/common';
import { DEFAULT_DATE_TIME_FORMAT, EV_TRACK_KEY } from '@/lib/constants';
import { trackEvent } from '@/lib/eventTracking';
import { ChevronRightPrimaryIcon } from '@/lib/icons';
import ServicesModal from '@/modules/booking/ServicesModal';
import { homeConfig } from '@/modules/home/homeConfig';
import useIsMobile from '@/shared-hooks/useIsMobile';
import { useNewBooking } from '../NewBookingProvider';

type Props = {
  availableCities: AvailableCity[];
};

export default function EVSelectionHeader({ availableCities }: Props) {
  const { t } = useTranslation(homeConfig.i18nNamespaces);
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  const { data: user } = useGetUserProfile();
  const { address, city } = useLocationStore();
  const { expectedEndTime, startTime } = useDateTimeStore();

  const handleOpen = () => {
    const user_agent = navigator.userAgent;
    const platform = detectPlatform(user_agent);
    trackEvent(EV_TRACK_KEY.ev_find_vehicle_button, {
      user_id: user?._id || null,
      userId: user?._id || null,
      platform,
      user_agent,
      city_id: city,
      location: address?.address,
      start_time: dayjs.unix(startTime).format(DEFAULT_DATE_TIME_FORMAT),
      end_time: dayjs.unix(expectedEndTime).format(DEFAULT_DATE_TIME_FORMAT),
      hour_renting: (expectedEndTime - startTime) / 3600,
    });
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const { serviceModels } = useNewBooking();

  return (
    <Grid container justifyContent="space-between">
      <Grid item xs={12} md={7}>
        <Typography
          variant={isMobile ? 'h4' : 'h1'}
          component="h2"
          color="grey.900"
          mb={isMobile ? 0 : 1}
        >
          {t('booking:ev.choose_your_right_vehicle')}
        </Typography>
        <Typography
          variant={isMobile ? 'caption' : 'h3'}
          component="h6"
          color="grey.600"
          fontWeight={500}
        >
          {t('booking:ev.make_your_optimal_choice')}
        </Typography>
      </Grid>
      <Grid item xs={12} md={5} textAlign={isMobile ? 'left' : 'right'}>
        <Button
          variant="text"
          endIcon={<ChevronRightPrimaryIcon />}
          onClick={handleOpen}
          sx={{ [`.${buttonClasses.endIcon}`]: { ml: 0 } }}
          data-test-id="openPricingDetailsBtn"
        >
          {t('booking:ev.pricing_details')}
        </Button>
        {serviceModels && serviceModels.length > 0 ? (
          <ServicesModal
            serviceList={serviceModels}
            availableCities={availableCities}
            open={open}
            handleClose={handleClose}
          />
        ) : null}
      </Grid>
    </Grid>
  );
}
